/**
 * <sk-audio-player> — native audio excerpt player.
 *
 * Data attributes (set in Liquid):
 *   data-start  (seconds) excerpt start            default 0
 *   data-end    (seconds) excerpt end (0 = to EOF) default 0
 *
 * Uses a single <audio preload="none"> so no bytes are fetched until
 * the visitor presses play. The waveform is static HTML bars; progress
 * is a second colored layer revealed via clip-path (no reflow).
 */
class SkAudioPlayer extends HTMLElement {
  constructor() {
    super();
    this.audio = this.querySelector('audio');
    this.playBtn = this.querySelector('[data-sk-ap-play]');
    this.wave = this.querySelector('[data-sk-ap-wave]');
    this.fill = this.querySelector('[data-sk-ap-fill]');
    this.timeEl = this.querySelector('[data-sk-ap-time]');
    this.start = parseFloat(this.dataset.start) || 0;
    this.end = parseFloat(this.dataset.end) || 0;

    if (!this.audio) return;

    this.playBtn.addEventListener('click', () => this._toggle());
    if (this.wave) this.wave.addEventListener('click', (e) => this._seek(e));
    this.audio.addEventListener('play', () => this.classList.add('is-playing'));
    this.audio.addEventListener('pause', () => this.classList.remove('is-playing'));
    this.audio.addEventListener('ended', () => this.classList.remove('is-playing'));
    this.audio.addEventListener('timeupdate', () => this._update());
    this.audio.addEventListener('loadedmetadata', () => this._update());
    this._update();
  }

  _duration() {
    if (this.end > this.start) return this.end - this.start;
    if (isFinite(this.audio.duration) && this.audio.duration > 0) {
      return Math.max(this.audio.duration - this.start, 0);
    }
    return 0;
  }

  _fmt(t) {
    t = Math.max(0, Math.round(t || 0));
    return Math.floor(t / 60) + ':' + String(t % 60).padStart(2, '0');
  }

  _toggle() {
    if (this.audio.paused) {
      const t = this.audio.currentTime;
      if (t < this.start || (this.end > 0 && t >= this.end - 0.05)) {
        this.audio.currentTime = this.start;
      }
      this.audio.play().catch(function () {});
    } else {
      this.audio.pause();
    }
  }

  _seek(e) {
    const dur = this._duration();
    if (dur <= 0) return;
    const rect = this.wave.getBoundingClientRect();
    const pct = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    this.audio.currentTime = this.start + pct * dur;
    this._update();
  }

  _update() {
    if (this.end > 0 && !this.audio.paused && this.audio.currentTime >= this.end) {
      this.audio.pause();
    }
    const dur = this._duration();
    const cur = Math.min(Math.max(this.audio.currentTime - this.start, 0), dur);
    const pct = dur > 0 ? (cur / dur) * 100 : 0;
    if (this.fill) this.fill.style.clipPath = 'inset(0 ' + (100 - pct) + '% 0 0)';
    if (this.timeEl) this.timeEl.textContent = this._fmt(cur) + ' / ' + this._fmt(dur);
  }
}

if (!customElements.get('sk-audio-player')) {
  customElements.define('sk-audio-player', SkAudioPlayer);
}
